import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { startZendeskOAuth, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { zendeskFormSchema, type ZendeskForm } from "./integrationFormSchemas";

export function ZendeskConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const { register, handleSubmit, formState: { errors } } = useForm<ZendeskForm>({
    resolver: zodResolver(zendeskFormSchema),
    defaultValues: {
      name: connection?.name ?? "Zendesk",
      subdomain: connection?.configSummary.subdomain ?? "",
      tags: connection?.configSummary.tags?.join(", ") ?? "io_spoke",
    },
  });

  const authorize = async (values: ZendeskForm) => {
    const tags = values.tags.split(",").map((value) => value.trim()).filter(Boolean).slice(0, 10);
    const { authorizationUrl } = await startZendeskOAuth({
      name: values.name,
      subdomain: values.subdomain.toLowerCase(),
      tags,
      ...(connection ? { connectionId: connection.id } : {}),
    });
    action.redirect(authorizationUrl);
  };

  const save = handleSubmit((values) => action.run(connection ? "save" : "authorize", async () => {
    try {
      if (!connection) return await authorize(values);
      await updateIntegrationConnection(connection.id, {
        name: values.name,
        config: { tags: values.tags.split(",").map((value) => value.trim()).filter(Boolean).slice(0, 10) },
      });
      toast.success("Zendesk settings updated.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect Zendesk.");
    }
  }));

  const reconnect = handleSubmit((values) => action.run("authorize", async () => {
    try { await authorize(values); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to authorize connection."); }
  }));

  return (
    <form className="space-y-4" onSubmit={save} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        IO Spoke will send you to Zendesk to approve ticket read/write access. Your Zendesk password never enters IO Spoke.
      </div>
      <FormField id="zendesk-name" label="Connection name" registration={register("name")} error={errors.name} />
      <FormField
        id="zendesk-subdomain"
        label="Zendesk subdomain"
        registration={register("subdomain")}
        error={errors.subdomain}
        disabled={Boolean(connection)}
        placeholder="acme"
      />
      <FormField id="zendesk-tags" label="Tags for created tickets" registration={register("tags")} error={errors.tags} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={connection ? action.saving : action.authorizing} loadingLabel={connection ? "Saving…" : "Authorizing…"}>
          {connection ? "Save settings" : <> <ExternalLink className="mr-2 h-4 w-4" />Continue to Zendesk</>}
        </Button>
        {connection ? (
          <Button type="button" variant="outline" onClick={reconnect} disabled={isSubmitting} loading={action.authorizing} loadingLabel="Authorizing…">
            Reconnect Zendesk
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
